import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getInventories, getItems, getVendors, getCategories, getBrands, restock, createVendor, lookupVendorPricing, addItemVendorPricing, lookupStockLevel, getSettings, getItemVendorPricing } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Plus, Trash2, Loader2, Package, CheckCircle, AlertCircle, ArrowLeft,
  Search, AlertTriangle, BarChart3, CalendarDays
} from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useAuth } from '../context/AuthContext';
import { ItemThumbnail } from '../components/ImageUploader';
import { RestockFilterRow } from './restock/RestockFilterRow';
import { SetPriceDialog } from './restock/SetPriceDialog';
import { AddVendorDialog } from './restock/AddVendorDialog';

const RESTOCK_PREFS_KEY = 'restock_prefs';

const Restock = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isManager } = useAuth();

  const [inventories, setInventories] = useState([]);
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    inventory_id: '',
    vendor_id: '',
    invoice_number: '',
    restock_date: new Date().toISOString().split('T')[0],
    line_items: []
  });

  // Item search state
  const [itemSearch, setItemSearch] = useState('');
  const [selectedItemForAdd, setSelectedItemForAdd] = useState(null);
  const searchRef = useRef(null);

  const [newLineItem, setNewLineItem] = useState({ item_id: '', quantity: '', unit_price: '' });

  // Package state
  const [itemPackages, setItemPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [packagesQty, setPackagesQty] = useState('');
  const [pricePerPackage, setPricePerPackage] = useState('');

  // Filter state for item search
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterSubCategory, setFilterSubCategory] = useState('all');
  const [filterBrand, setFilterBrand] = useState('all');

  // Stock level display
  const [currentStock, setCurrentStock] = useState(null);
  const [stockLoading, setStockLoading] = useState(false);

  // Price lookup state
  const [priceLookupLoading, setPriceLookupLoading] = useState(false);
  const [priceAutoFilled, setPriceAutoFilled] = useState(false);

  // Dialogs
  const [noPricingItem, setNoPricingItem] = useState(null);
  const [setPriceOpen, setSetPriceOpen] = useState(false);
  const [setPriceData, setSetPriceData] = useState({ unit_price: '', vendor_sku: '', vendor_item_name: '' });
  const [savingPrice, setSavingPrice] = useState(false);
  const [addVendorDialogOpen, setAddVendorDialogOpen] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');
  const [savingVendor, setSavingVendor] = useState(false);
  const [vendorChangeMessage, setVendorChangeMessage] = useState('');
  const [taxRate, setTaxRate] = useState(0);

  // Load saved preferences
  const getSavedPrefs = () => {
    try { return JSON.parse(localStorage.getItem(RESTOCK_PREFS_KEY) || '{}'); } catch { return {}; }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [invRes, itemsRes, vendorRes, settingsRes, catRes, brandRes] = await Promise.all([
          getInventories(), getItems(), getVendors(true), getSettings(), getCategories(), getBrands()
        ]);
        setInventories(invRes.data);
        setItems(itemsRes.data);
        setVendors(vendorRes.data);
        setTaxRate(settingsRes.data.sales_tax_rate || 0);
        setCategories(catRes.data);
        setBrands(brandRes.data);

        const savedState = searchParams.get('restore');
        const prefs = getSavedPrefs();
        
        if (savedState) {
          try {
            const restored = JSON.parse(atob(savedState));
            setFormData({
              inventory_id: restored.inventory_id || invRes.data[0]?.id || '',
              vendor_id: restored.vendor_id || vendorRes.data[0]?.id || '',
              invoice_number: restored.invoice_number || '',
              line_items: restored.line_items || []
            });
            if (restored.pending_item_id) {
              const item = itemsRes.data.find(i => i.id === restored.pending_item_id);
              if (item) {
                setSelectedItemForAdd(item);
                setItemSearch(item.item_name);
                setNewLineItem(prev => ({ ...prev, item_id: item.id }));
              }
            }
            toast.success('Restock state restored');
          } catch (e) { console.warn('Failed to restore restock state:', e); }
        } else {
          setFormData(prev => ({
            ...prev,
            inventory_id: prefs.inventory_id || invRes.data[0]?.id || '',
            vendor_id: prefs.vendor_id || vendorRes.data[0]?.id || ''
          }));
        }
      } catch (err) {
        toast.error('Failed to load restock data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save preferences when vendor/inventory changes
  useEffect(() => {
    if (formData.vendor_id || formData.inventory_id) {
      localStorage.setItem(RESTOCK_PREFS_KEY, JSON.stringify({
        vendor_id: formData.vendor_id,
        inventory_id: formData.inventory_id
      }));
    }
  }, [formData.vendor_id, formData.inventory_id]);

  // Category helpers
  const parentCategories = categories.filter(c => !c.parent_id);
  const getSubcategories = (parentId) => categories.filter(c => c.parent_id === parentId);
  const filterSubcategories = filterCategory !== 'all' ? getSubcategories(filterCategory) : categories.filter(c => c.parent_id);
  const getCategoryName = (catId) => {
    const cat = categories.find(c => c.id === catId);
    if (!cat) return '';
    const parent = cat.parent_id ? categories.find(c => c.id === cat.parent_id) : null;
    return parent ? `${parent.category_name} > ${cat.category_name}` : cat.category_name;
  };
  const getBrandName = (brandId) => brands.find(b => b.id === brandId)?.brand_name || '';

  // Reset subcategory when category changes
  useEffect(() => { setFilterSubCategory('all'); }, [filterCategory]);

  // Filtered items list (always visible grid)
  const filteredItems = items.filter(item => {
    // Apply category filter
    if (filterCategory !== 'all') {
      const childIds = getSubcategories(filterCategory).map(c => c.id);
      if (!childIds.includes(item.category_id)) return false;
    }
    // Apply subcategory filter
    if (filterSubCategory !== 'all' && item.category_id !== filterSubCategory) return false;
    // Apply brand filter
    if (filterBrand !== 'all' && item.brand_id !== filterBrand) return false;
    // Apply text search
    if (!itemSearch.trim()) return true;
    const q = itemSearch.toLowerCase();
    return item.item_name.toLowerCase().includes(q) ||
           (item.internal_code || '').toLowerCase().includes(q) ||
           (item.vendor_skus || []).some(sku => sku.toLowerCase().includes(q));
  });

  // Lookup stock level
  const fetchStockLevel = useCallback(async (itemId, inventoryId) => {
    if (!itemId || !inventoryId) { setCurrentStock(null); return; }
    setStockLoading(true);
    try {
      const res = await lookupStockLevel(itemId, inventoryId);
      setCurrentStock(res.data);
    } catch { setCurrentStock(null); }
    finally { setStockLoading(false); }
  }, []);

  // Lookup price
  const lookupPrice = useCallback(async (itemId, vendorId) => {
    if (!itemId || !vendorId) return;
    setPriceLookupLoading(true);
    setPriceAutoFilled(false);
    try {
      const res = await lookupVendorPricing(itemId, vendorId);
      if (res.data.found) {
        setNewLineItem(prev => ({ ...prev, unit_price: String(res.data.unit_price) }));
        setPriceAutoFilled(true);
      } else {
        setNewLineItem(prev => ({ ...prev, unit_price: '' }));
        // Directly open the inline Set Price popup — no other options
        const item = items.find(i => i.id === itemId);
        const vendor = vendors.find(v => v.id === vendorId);
        setNoPricingItem({ itemId, itemName: item?.item_name || 'Unknown', vendorId, vendorName: vendor?.vendor_name || 'Unknown' });
        setSetPriceData({ unit_price: '', vendor_sku: '', vendor_item_name: item?.item_name || '' });
        setSetPriceOpen(true);
      }
    } catch (err) { console.warn('Price lookup failed:', err); }
    finally { setPriceLookupLoading(false); }
  }, [items, vendors]);

  const handleItemSelect = async (item) => {
    setSelectedItemForAdd(item);
    setItemSearch(item.item_name);
    setNewLineItem(prev => ({ ...prev, item_id: item.id, unit_price: '' }));
    setPriceAutoFilled(false);
    // Fetch vendor pricing records for this item+vendor
    try {
      const pricingRes = await getItemVendorPricing(item.id);
      // Filter to current vendor
      const vendorPricings = pricingRes.data.filter(p => p.vendor_id === formData.vendor_id);
      setItemPackages(vendorPricings);
      if (vendorPricings.length > 0) {
        const defaultPkg = vendorPricings.find(p => p.units_per_package === 1) || vendorPricings[0];
        setSelectedPackage(defaultPkg);
        setPackagesQty('');
        setPricePerPackage(String(defaultPkg.package_price || defaultPkg.unit_price * (defaultPkg.units_per_package || 1)));
        setPriceAutoFilled(true);
      } else {
        setSelectedPackage(null);
        setItemPackages([]);
      }
    } catch { setItemPackages([]); setSelectedPackage(null); }
    // Lookup stock
    fetchStockLevel(item.id, formData.inventory_id);
    if (formData.vendor_id && !selectedPackage) lookupPrice(item.id, formData.vendor_id);
  };

  const handleVendorChange = (vendorId) => {
    const hadItems = formData.line_items.length > 0;
    setFormData(prev => ({
      ...prev,
      vendor_id: vendorId,
      invoice_number: ''  // Always clear invoice on vendor change
    }));
    if (hadItems) {
      setVendorChangeMessage('Vendor has been changed. Please enter a new invoice number. Invoice numbers are vendor-specific.');
    } else {
      setVendorChangeMessage('');
    }
    // Re-lookup price for pending item if any
    if (newLineItem.item_id) {
      setNewLineItem(prev => ({ ...prev, unit_price: '' }));
      setPriceAutoFilled(false);
      lookupPrice(newLineItem.item_id, vendorId);
    }
  };

  const handleInventoryChange = (inventoryId) => {
    setFormData(prev => ({ ...prev, inventory_id: inventoryId }));
    if (newLineItem.item_id) fetchStockLevel(newLineItem.item_id, inventoryId);
  };

  const addLineItem = () => {
    const hasPackage = !!selectedPackage;
    
    // Validate based on mode
    if (hasPackage) {
      if (!newLineItem.item_id || !packagesQty || !pricePerPackage) {
        setError('Please select an item, enter packages qty and price per package');
        return;
      }
      if (parseInt(packagesQty) <= 0) { setError('Packages quantity must be greater than 0'); return; }
      if (parseFloat(pricePerPackage) <= 0) { setError('Price per package must be greater than 0'); return; }
    } else {
      if (!newLineItem.item_id || !newLineItem.quantity || !newLineItem.unit_price) {
        setError('Please select an item and fill in quantity and unit price');
        return;
      }
      if (parseInt(newLineItem.quantity) <= 0) { setError('Quantity must be greater than 0'); return; }
      if (parseFloat(newLineItem.unit_price) <= 0) { setError('Unit price must be greater than 0'); return; }
    }

    const item = items.find(i => i.id === newLineItem.item_id);
    if (formData.line_items.some(li => li.item_id === newLineItem.item_id && li.package_id === (selectedPackage?.id || null))) {
      setError('This item+package combination is already in the list.');
      return;
    }

    let totalUnits, unitCost, lineTotal;
    if (hasPackage) {
      const pkgQty = parseInt(packagesQty);
      const pkgPrice = parseFloat(pricePerPackage);
      totalUnits = pkgQty * selectedPackage.units_per_package;
      unitCost = pkgPrice / selectedPackage.units_per_package;
      lineTotal = pkgQty * pkgPrice;
    } else {
      totalUnits = parseInt(newLineItem.quantity);
      unitCost = parseFloat(newLineItem.unit_price);
      lineTotal = totalUnits * unitCost;
    }

    setFormData(prev => ({
      ...prev,
      line_items: [...prev.line_items, {
        item_id: newLineItem.item_id,
        quantity: totalUnits,
        unit_price: Math.round(unitCost * 10000) / 10000,
        item_name: item?.item_name || 'Unknown',
        internal_code: item?.internal_code || '',
        current_stock: currentStock?.quantity || 0,
        package_id: hasPackage ? selectedPackage.id : null,
        packages_qty: hasPackage ? parseInt(packagesQty) : null,
        price_per_package: hasPackage ? parseFloat(pricePerPackage) : null,
        package_label: hasPackage ? selectedPackage.package_label : null,
        units_per_package: hasPackage ? selectedPackage.units_per_package : 1
      }]
    }));
    setNewLineItem({ item_id: '', quantity: '', unit_price: '' });
    setSelectedItemForAdd(null);
    setItemSearch('');
    setPriceAutoFilled(false);
    setCurrentStock(null);
    setItemPackages([]);
    setSelectedPackage(null);
    setPackagesQty('');
    setPricePerPackage('');
    setError('');
  };

  const removeLineItem = (index) => {
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  const handleSavePriceInline = async () => {
    if (!setPriceData.unit_price || parseFloat(setPriceData.unit_price) <= 0) {
      toast.error('Enter a valid price');
      return;
    }
    setSavingPrice(true);
    try {
      await addItemVendorPricing(noPricingItem.itemId, {
        vendor_id: noPricingItem.vendorId,
        vendor_item_name: setPriceData.vendor_item_name,
        vendor_sku: setPriceData.vendor_sku,
        unit_price: parseFloat(setPriceData.unit_price),
        unit: items.find(i => i.id === noPricingItem.itemId)?.unit || 'PCS'
      });
      toast.success('Price saved');
      setSetPriceOpen(false);
      setNewLineItem(prev => ({ ...prev, unit_price: setPriceData.unit_price }));
      setPriceAutoFilled(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save price');
    } finally { setSavingPrice(false); }
  };

  const handleAddVendor = async () => {
    if (!newVendorName.trim()) return;
    setSavingVendor(true);
    try {
      const res = await createVendor({ vendor_name: newVendorName, active: true });
      setVendors([...vendors, res.data]);
      setFormData({ ...formData, vendor_id: res.data.id });
      setNewVendorName('');
      setAddVendorDialogOpen(false);
    } catch (err) { toast.error('Failed to save vendor'); }
    finally { setSavingVendor(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.inventory_id) { setError('Please select a destination inventory'); return; }
    if (!formData.vendor_id) { setError('Please select a vendor'); return; }
    if (!formData.invoice_number.trim()) { setError('Please enter an invoice number'); return; }
    if (formData.line_items.length === 0) { setError('Please add at least one item'); return; }
    setSubmitting(true);
    setError('');
    try {
      await restock({
        inventory_id: formData.inventory_id,
        vendor_id: formData.vendor_id,
        invoice_number: formData.invoice_number.trim(),
        restock_date: formData.restock_date,
        line_items: formData.line_items.map(li => ({
          item_id: li.item_id, quantity: li.quantity, unit_price: li.unit_price,
          package_id: li.package_id || null,
          packages_qty: li.packages_qty || null,
          price_per_package: li.price_per_package || null
        }))
      });
      setSuccess(true);
      setTimeout(() => navigate('/inventory'), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to process restock');
    } finally { setSubmitting(false); }
  };

  const totalValue = formData.line_items.reduce((sum, li) => sum + (li.quantity * li.unit_price), 0);
  const taxAmount = totalValue * (taxRate / 100);
  const totalWithTax = totalValue + taxAmount;
  const selectedVendor = vendors.find(v => v.id === formData.vendor_id);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-[#0776BD]" />
      </div>
    );
  }

  if (success) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] animate-slide-in">
        <div className="bg-green-100 p-6 rounded-full mb-4">
          <CheckCircle size={64} className="text-green-600" />
        </div>
        <h2 className="heading-2 text-green-600 mb-2">Restock Complete!</h2>
        <p className="text-[#6B7280]">{formData.line_items.length} item(s) restocked for ${totalWithTax.toFixed(2)}</p>
        <p className="text-sm text-[#9CA3AF] mt-1">Invoice: {formData.invoice_number}</p>
        <p className="text-sm text-[#9CA3AF]">Redirecting to inventory...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="restock-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="hover:bg-[#E6F2FA]" data-testid="back-btn">
          <ArrowLeft size={20} className="text-[#0776BD]" />
        </Button>
        <div>
          <h1 className="heading-2 text-[#1F2937]">Restock Inventory</h1>
          <p className="text-[#6B7280]">Add new stock from vendor shipment</p>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="rounded-lg">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,3.5in)_1fr] gap-6">
          {/* Left Column - Shipment Info (max 3.5in / 336px) */}
          <Card className="bg-white rounded-xl border border-gray-200 shadow-soft w-full lg:max-w-[3.5in]">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-[#1F2937]">Shipment Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#1F2937]">Destination Inventory *</Label>
                <Select value={formData.inventory_id} onValueChange={handleInventoryChange}>
                  <SelectTrigger className="h-12 rounded-lg border-gray-300" data-testid="restock-inventory-select">
                    <SelectValue placeholder="Select inventory" />
                  </SelectTrigger>
                  <SelectContent>
                    {inventories.map(inv => <SelectItem key={inv.id} value={inv.id}>{inv.inventory_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#1F2937]">Vendor *</Label>
                <div className="flex gap-2">
                  <Select value={formData.vendor_id} onValueChange={handleVendorChange}>
                    <SelectTrigger className="h-12 rounded-lg border-gray-300 flex-1" data-testid="restock-vendor-select">
                      <SelectValue placeholder="Select vendor" />
                    </SelectTrigger>
                    <SelectContent>
                      {vendors.map(vendor => <SelectItem key={vendor.id} value={vendor.id}>{vendor.vendor_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {isManager() && (
                    <Button type="button" variant="outline" size="icon" className="h-12 w-12 rounded-lg" onClick={() => setAddVendorDialogOpen(true)} title="Add New Vendor">
                      <Plus size={18} />
                    </Button>
                  )}
                </div>
                {selectedVendor && (
                  <div className="text-xs text-[#6B7280] mt-1">
                    {selectedVendor.phone_number && <span>{selectedVendor.phone_number} &bull; </span>}
                    {selectedVendor.email}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#1F2937] flex items-center gap-1.5">
                  Invoice Number <span className="text-red-500">*</span>
                  {!formData.invoice_number.trim() && (
                    <span className="ml-auto text-[11px] font-medium text-red-600 bg-red-50 border border-red-200 rounded-full px-2 py-0.5" data-testid="invoice-required-badge">Required</span>
                  )}
                </Label>
                <Input
                  value={formData.invoice_number}
                  onChange={(e) => { setFormData({ ...formData, invoice_number: e.target.value }); if (e.target.value.trim()) setVendorChangeMessage(''); }}
                  placeholder="e.g., INV-2024-001"
                  className={`h-12 rounded-lg ${
                    !formData.invoice_number.trim() ? 'border-red-400 focus:border-red-500 bg-red-50/40' :
                    vendorChangeMessage ? 'border-amber-400' : 'border-gray-300'
                  }`}
                  data-testid="invoice-input"
                />
                {!formData.invoice_number.trim() && (
                  <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2" data-testid="missing-invoice-notice">
                    <AlertCircle size={16} className="text-red-500 shrink-0 mt-0.5" />
                    <p className="text-xs text-red-700">
                      <span className="font-semibold">Invoice Number is required.</span> Enter it here to enable the <span className="font-mono">Complete Restock</span> button.
                    </p>
                  </div>
                )}
                {vendorChangeMessage && formData.invoice_number.trim() && (
                  <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2" data-testid="vendor-change-message">
                    <AlertTriangle size={16} className="text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700">{vendorChangeMessage}</p>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#1F2937] flex items-center gap-1">
                  <CalendarDays size={14} /> Restock Date
                </Label>
                <Input
                  type="date"
                  value={formData.restock_date}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setFormData({ ...formData, restock_date: e.target.value })}
                  className="h-12 rounded-lg border-gray-300"
                  data-testid="restock-date-input"
                />
              </div>

              {/* Invoice Summary */}
              <div className="pt-4 border-t border-gray-200 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-[#6B7280]">Total Items:</span>
                  <span className="font-bold text-[#1F2937]">{formData.line_items.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#6B7280]">Total Qty:</span>
                  <span className="font-bold text-[#1F2937]">{formData.line_items.reduce((s, li) => s + li.quantity, 0)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#6B7280]">Subtotal:</span>
                  <span className="font-bold text-[#1F2937]">${totalValue.toFixed(2)}</span>
                </div>
                {taxRate > 0 && (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-[#6B7280]">Tax ({taxRate}%):</span>
                      <span className="font-medium text-[#1F2937]">${taxAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                      <span className="text-[#6B7280] font-medium">Total After Tax:</span>
                      <span className="font-bold text-xl text-[#0776BD]" data-testid="total-with-tax">${totalWithTax.toFixed(2)}</span>
                    </div>
                  </>
                )}
                {taxRate === 0 && (
                  <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                    <span className="text-[#6B7280] font-medium">Invoice Total:</span>
                    <span className="font-bold text-xl text-[#0776BD]">${totalValue.toFixed(2)}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Right Column - Line Items */}
          <Card className="bg-white rounded-xl border border-gray-200 shadow-soft min-w-0">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-[#1F2937] flex items-center gap-2">
                <Package size={20} className="text-[#0776BD]" /> Items to Restock
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add Item Form - Smart Search */}
              <div className="p-4 bg-[#F5F6F7] rounded-lg border-2 border-dashed border-gray-300 space-y-3">
                {/* Filter row */}
                <RestockFilterRow
                  filterCategory={filterCategory} setFilterCategory={setFilterCategory}
                  filterSubCategory={filterSubCategory} setFilterSubCategory={setFilterSubCategory}
                  filterBrand={filterBrand} setFilterBrand={setFilterBrand}
                  parentCategories={parentCategories}
                  filterSubcategories={filterSubcategories}
                  brands={brands}
                  itemSearch={itemSearch}
                  onClearAll={() => { setFilterCategory('all'); setFilterSubCategory('all'); setFilterBrand('all'); setItemSearch(''); }}
                />

                {/* Search input (full-width) */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" size={18} />
                  <Input
                    ref={searchRef}
                    value={itemSearch}
                    onChange={(e) => {
                      setItemSearch(e.target.value);
                      if (!e.target.value) {
                        setSelectedItemForAdd(null);
                        setNewLineItem(prev => ({ ...prev, item_id: '', unit_price: '' }));
                        setCurrentStock(null);
                        setPriceAutoFilled(false);
                      }
                    }}
                    placeholder="Search by item name, code, or vendor SKU..."
                    className="pl-10 h-10 rounded-lg border-gray-300"
                    data-testid="item-search-input"
                    autoComplete="off"
                  />
                </div>

                {/* Always-visible item grid - 1 column with stacked metadata */}
                <div className="grid grid-cols-1 gap-2 max-h-[420px] overflow-y-auto pr-1">
                  {filteredItems.map(item => {
                    const isSelected = selectedItemForAdd?.id === item.id;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => handleItemSelect(item)}
                        data-testid={`restock-stock-item-${item.id}`}
                        className={`p-3 border rounded-lg text-left transition-all flex items-start gap-3 ${
                          isSelected ? 'border-[#0776BD] bg-[#E6F2FA] ring-2 ring-[#0776BD]/20' :
                          'border-gray-200 hover:border-[#0776BD] hover:bg-[#E6F2FA]/50'
                        }`}
                      >
                        <ItemThumbnail imageIds={item.image_ids} size={44} alt={item.item_name} />
                        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                          <p className="font-medium text-[#1F2937] text-sm leading-tight">{item.item_name}{getBrandName(item.brand_id) ? <span className="text-[#6B7280] font-normal"> · {getBrandName(item.brand_id)}</span> : null}</p>
                          <p className="text-xs text-[#6B7280] leading-tight">{getCategoryName(item.category_id) || <span className="italic text-[#9CA3AF]">Uncategorized</span>}</p>
                          <p className="text-xs text-[#0776BD] font-mono leading-tight">{item.internal_code}</p>
                          {item.vendor_skus?.length > 0 && (
                            <p className="text-[11px] text-[#9CA3AF] truncate">SKU: {item.vendor_skus.join(', ')}</p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                  {filteredItems.length === 0 && (
                    <div className="py-10 text-center text-[#6B7280] border-2 border-dashed border-gray-300 rounded-lg">
                      No items match your filters
                    </div>
                  )}
                </div>

                {/* Add Details row (appears only when an item is selected) */}
                {selectedItemForAdd && (
                  <div className="border-2 border-[#0776BD]/30 bg-white rounded-lg p-3 space-y-3" data-testid="restock-add-details">
                    {/* Selected item chip */}
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="px-3 py-1.5 bg-[#E6F2FA] border border-[#0776BD]/20 rounded-lg flex items-center gap-2">
                        <Package size={14} className="text-[#0776BD] shrink-0" />
                        <span className="text-sm font-medium text-[#1F2937]">{selectedItemForAdd.item_name}</span>
                        <span className="font-mono text-xs text-[#6B7280]">{selectedItemForAdd.internal_code}</span>
                        <button type="button" onClick={() => { setSelectedItemForAdd(null); setItemSearch(''); setNewLineItem(prev => ({ ...prev, item_id: '', unit_price: '' })); setCurrentStock(null); setPriceAutoFilled(false); }} className="text-[#9CA3AF] hover:text-red-500 ml-1" data-testid="clear-selected-item-btn">
                          <Trash2 size={14} />
                        </button>
                      </div>
                      {stockLoading ? (
                        <span className="text-xs text-[#6B7280]"><Loader2 size={12} className="inline animate-spin" /> Checking stock...</span>
                      ) : currentStock ? (
                        <Badge className={`text-xs px-2 py-0.5 ${currentStock.is_low_stock ? 'bg-red-100 text-red-700 border-red-200' : 'bg-green-100 text-green-700 border-green-200'}`} data-testid="current-stock-badge">
                          <BarChart3 size={12} className="mr-1" />
                          Current Stock: {currentStock.quantity} {currentStock.is_low_stock ? '(Low Stock)' : ''}
                        </Badge>
                      ) : (
                        <Badge className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 border-gray-200">
                          New — no stock yet
                        </Badge>
                      )}
                    </div>

                    {/* Input row: Package (if any) + Qty + Price + Add */}
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end">
                      {itemPackages.length > 0 ? (
                        <div>
                          <Label className="text-sm font-medium text-[#1F2937]">Package</Label>
                          <Select value={selectedPackage?.id || ''} onValueChange={(val) => {
                            const pkg = itemPackages.find(p => p.id === val);
                            setSelectedPackage(pkg);
                            setPackagesQty('');
                            setPricePerPackage(String(pkg?.package_price || pkg?.unit_price * (pkg?.units_per_package || 1) || ''));
                            setPriceAutoFilled(true);
                          }}>
                            <SelectTrigger className="h-10 rounded-lg border-gray-300 mt-1" data-testid="package-select">
                              <SelectValue placeholder="Select package" />
                            </SelectTrigger>
                            <SelectContent>
                              {itemPackages.map(pkg => (
                                <SelectItem key={pkg.id} value={pkg.id}>
                                  {pkg.package_label || 'Single'} ({pkg.units_per_package || 1}×) — ${(pkg.package_price || pkg.unit_price * (pkg.units_per_package || 1)).toFixed(2)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : <div />}

                      {selectedPackage ? (
                        <>
                          <div>
                            <Label className="text-sm font-medium text-[#1F2937]">Packages Qty *</Label>
                            <Input type="number" min="1" value={packagesQty} onChange={(e) => setPackagesQty(e.target.value)} placeholder="0" className="h-10 rounded-lg border-gray-300 mt-1" data-testid="add-packages-qty-input" />
                          </div>
                          <div>
                            <Label className="text-sm font-medium text-[#1F2937]">Price/Package</Label>
                            <Input type="number" min="0.01" step="0.01" value={pricePerPackage} onChange={(e) => setPricePerPackage(e.target.value)} placeholder="$0.00" className="h-10 rounded-lg border-gray-300 mt-1" data-testid="add-price-per-package-input" />
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <Label className="text-sm font-medium text-[#1F2937]">Quantity *</Label>
                            <Input type="number" min="1" value={newLineItem.quantity} onChange={(e) => setNewLineItem({ ...newLineItem, quantity: e.target.value })} placeholder="0" className="h-10 rounded-lg border-gray-300 mt-1" data-testid="add-quantity-input" />
                          </div>
                          <div>
                            <Label className="text-sm font-medium text-[#1F2937] flex items-center gap-1">
                              Unit Price
                              {priceLookupLoading && <Loader2 size={12} className="animate-spin text-[#0776BD]" />}
                            </Label>
                            <div
                              className={`relative mt-1 h-10 flex items-center rounded-lg border px-3 text-sm ${
                                priceAutoFilled
                                  ? 'border-green-400 bg-green-50'
                                  : newLineItem.item_id && !priceLookupLoading
                                    ? 'border-amber-400 bg-amber-50 cursor-pointer hover:bg-amber-100 transition-colors'
                                    : 'border-gray-300 bg-gray-50'
                              }`}
                              onClick={() => {
                                if (newLineItem.item_id && !priceAutoFilled && !priceLookupLoading) {
                                  const item = items.find(i => i.id === newLineItem.item_id);
                                  const vendor = vendors.find(v => v.id === formData.vendor_id);
                                  setNoPricingItem({ itemId: newLineItem.item_id, itemName: item?.item_name || '', vendorId: formData.vendor_id, vendorName: vendor?.vendor_name || '' });
                                  setSetPriceData({ unit_price: '', vendor_sku: '', vendor_item_name: item?.item_name || '' });
                                  setSetPriceOpen(true);
                                }
                              }}
                              title={newLineItem.item_id && !priceAutoFilled && !priceLookupLoading ? 'Price not set. Click to define price.' : ''}
                              data-testid="add-price-input"
                            >
                              {newLineItem.unit_price ? (
                                <span className="text-[#1F2937] font-medium">${parseFloat(newLineItem.unit_price).toFixed(2)}</span>
                              ) : (
                                <span className="text-[#9CA3AF]">{newLineItem.item_id ? (priceLookupLoading ? '...' : 'Click to set') : '0.00'}</span>
                              )}
                              {priceAutoFilled && (
                                <Badge className="absolute -top-2 right-1 bg-green-100 text-green-700 border-0 text-[10px] px-1.5 py-0">
                                  Auto
                                </Badge>
                              )}
                              {newLineItem.item_id && !priceAutoFilled && !priceLookupLoading && (
                                <span className="absolute -top-2 right-1 bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0 rounded-full font-medium">
                                  Set Price
                                </span>
                              )}
                            </div>
                          </div>
                        </>
                      )}

                      <Button type="button" onClick={addLineItem} disabled={
                        selectedPackage
                          ? !newLineItem.item_id || !packagesQty || !pricePerPackage
                          : !newLineItem.item_id || !newLineItem.quantity || !newLineItem.unit_price || !priceAutoFilled
                      } className="h-10 px-4 bg-[#0776BD] hover:bg-[#055A92] rounded-lg" data-testid="add-line-item-btn">
                        <Plus size={18} className="mr-1" /> Add
                      </Button>
                    </div>

                    {/* Package calculation preview */}
                    {selectedPackage && packagesQty && pricePerPackage && (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm" data-testid="package-preview">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          <div>
                            <span className="text-blue-600 text-xs">Package</span>
                            <p className="font-medium text-[#1F2937]">{selectedPackage.package_label}</p>
                          </div>
                          <div>
                            <span className="text-blue-600 text-xs">Total Units</span>
                            <p className="font-medium text-[#1F2937]">{parseInt(packagesQty) * selectedPackage.units_per_package}</p>
                          </div>
                          <div>
                            <span className="text-blue-600 text-xs">Unit Cost</span>
                            <p className="font-medium text-[#1F2937]">${(parseFloat(pricePerPackage) / selectedPackage.units_per_package).toFixed(2)}</p>
                          </div>
                          <div>
                            <span className="text-blue-600 text-xs">Line Total</span>
                            <p className="font-medium text-[#1F2937]">${(parseInt(packagesQty) * parseFloat(pricePerPackage)).toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Line Items Table */}
              {formData.line_items.length > 0 ? (
                <div>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F5F6F7]">
                        <TableHead className="font-semibold text-[#1F2937]">Item</TableHead>
                        <TableHead className="font-semibold text-[#1F2937] text-center">Current</TableHead>
                        <TableHead className="font-semibold text-[#1F2937] text-center">+ Qty</TableHead>
                        <TableHead className="font-semibold text-[#1F2937] text-right">Unit Price</TableHead>
                        <TableHead className="font-semibold text-[#1F2937] text-right">Total</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formData.line_items.map((li, index) => (
                        <TableRow key={li.package_id || `${li.item_id}-${index}`} data-testid={`line-item-${index}`}>
                          <TableCell>
                            <p className="font-medium text-[#1F2937]">{li.item_name}</p>
                            <p className="text-xs text-[#6B7280] font-mono">{li.internal_code}</p>
                            {li.package_label && (
                              <p className="text-xs text-blue-600">{li.packages_qty} x {li.package_label} ({li.units_per_package} unit{li.units_per_package > 1 ? 's' : ''} each)</p>
                            )}
                          </TableCell>
                          <TableCell className="text-center text-[#6B7280] text-sm">{li.current_stock || 0}</TableCell>
                          <TableCell className="text-center font-medium text-[#0776BD]">+{li.quantity}</TableCell>
                          <TableCell className="text-right text-[#6B7280]">${li.unit_price.toFixed(2)}</TableCell>
                          <TableCell className="text-right font-medium text-[#1F2937]">${(li.quantity * li.unit_price).toFixed(2)}</TableCell>
                          <TableCell>
                            <Button type="button" variant="ghost" size="icon" onClick={() => removeLineItem(index)} className="hover:bg-red-50 text-red-500" data-testid={`remove-line-${index}`}>
                              <Trash2 size={18} />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <div className="flex justify-end mt-2 pr-16 gap-4">
                    <span className="text-[#6B7280]">Subtotal: ${totalValue.toFixed(2)}</span>
                    {taxRate > 0 && (
                      <>
                        <span className="text-[#6B7280]">Tax: ${taxAmount.toFixed(2)}</span>
                        <span className="font-bold text-[#0776BD]">Total: ${totalWithTax.toFixed(2)}</span>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="py-8 text-center text-[#6B7280] border-2 border-dashed border-gray-300 rounded-lg">
                  <Package size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No items added yet</p>
                  <p className="text-sm">Search for an item above to get started</p>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex flex-col items-end gap-2 pt-4">
                {formData.line_items.length > 0 && !formData.invoice_number.trim() && (
                  <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="missing-invoice-notice-bottom">
                    <AlertCircle size={16} className="text-red-500 shrink-0" />
                    <span>Enter the Invoice Number on the left panel to enable Complete Restock.</span>
                  </div>
                )}
                <Button type="submit" disabled={submitting || formData.line_items.length === 0 || !formData.invoice_number.trim()} className="bg-[#0776BD] hover:bg-[#055A92] px-8 rounded-lg" data-testid="submit-restock-btn">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Complete Restock ({formData.line_items.length} item{formData.line_items.length !== 1 ? 's' : ''})
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </form>

      {/* Set Price Inline Dialog */}
      <SetPriceDialog
        open={setPriceOpen}
        onOpenChange={setSetPriceOpen}
        noPricingItem={noPricingItem}
        setPriceData={setPriceData}
        setSetPriceData={setSetPriceData}
        savingPrice={savingPrice}
        onSave={handleSavePriceInline}
        showSwitchVendorWarning={formData.line_items.length > 0}
      />

      {/* Add Vendor Dialog */}
      <AddVendorDialog
        open={addVendorDialogOpen}
        onOpenChange={setAddVendorDialogOpen}
        vendorName={newVendorName}
        setVendorName={setNewVendorName}
        saving={savingVendor}
        onSave={handleAddVendor}
      />
    </div>
  );
};

export default Restock;
